import React from 'react';
import { Vector3 } from 'three';

import { Bearing } from '@paragon/contract-library';
import { Direction2D } from '@paragon/geometry-library';
import { calculateBearingVisualData } from '@paragon/three-shared';

import { useAppDispatch, useAppSelector } from '../../components/hooks';
import { shiftBearingPosition } from '../../store/actions/editBearingActions';
import { getActiveComponentDesign } from '../../store/selectors/getActiveComponentDesignSelector';
import { R3FSvgVisual } from '../util/R3FSvgVisual';
import { NudgeHandle } from './NudgeHandle';

const SIZE = 4;
const MIN_EXPAND = 3 / 8;

/** Places nudge arrow hitboxes flush with edges of the bearing while respecting a minimum. */
const calculateExpand = (width: number) => Math.max(MIN_EXPAND, width / (2 * SIZE) + 0.25);

export const BearingEditorHandle = ({ bearing }: { bearing: Bearing }) => {
  const dispatch = useAppDispatch();

  const componentDesign = useAppSelector(getActiveComponentDesign)!;
  const bearingVisualData = calculateBearingVisualData(bearing, componentDesign.component);
  if (!bearingVisualData) return null;
  const { position, height, normal, width } = bearingVisualData;
  if (!normal.equals(Direction2D.up)) return null;

  const handlePosition = new Vector3(position.x, position.y - height * 0.75);

  let anchorIcon = null;
  if (!bearing.relativeGeometry) {
    const anchorOffset =
      bearing.anchor === 'Left' ? -bearing.width / 2 : bearing.anchor === 'Right' ? bearing.width / 2 : 0;
    const anchorPosition = new Vector3(position.x - anchorOffset, position.y - height / 2);
    anchorIcon = <R3FSvgVisual iconName="icon-anchor" rotation={0} size={0.8} position={anchorPosition} />;
  }

  return (
    <>
      {anchorIcon}
      <NudgeHandle
        editType="EditingBearing"
        leftRight
        moveCallback={(deltaX: number) => dispatch(shiftBearingPosition(bearing, componentDesign.guid, deltaX))}
        position={handlePosition}
        size={SIZE}
        expand={calculateExpand(width)}
        coordinateUpDirection={Direction2D.up}
        settingsButton={{ type: 'Snapping', position: { x: 0, y: 0, z: 0 } }}
      />
    </>
  );
};
